import { storeToRefs } from "pinia";
import { computed, onMounted, ref, toRefs } from "vue";

import { $streemApiV1 } from "shared/boot/api";
import { targetType } from "shared/helpers/mentions";
import { isSocialStream, isStream } from "shared/helpers/stream";
import features from "shared/services/features";
import { type SimilarMentionsStream } from "shared/services/fetch/fetchSimilarMentions";
import { useBookmarkStreamsStore } from "shared/stores/bookmarkStreams";
import { useStreamsStore } from "shared/stores/streams";
import { type Nullable, type Stream } from "shared/types";
import {
  type Bookmark,
  type Mention,
  type SyndicationKey,
} from "shared/types/mentions";

import useTeamPicker from "./useTeamPicker";

export interface BookmarksProps {
  mention: Mention;
  product: Nullable<string>;
  sourceStream: Nullable<Stream | SimilarMentionsStream>;
}

export type BookmarkStreamsForMentionsResponseData = Record<
  SyndicationKey,
  number[]
>;

export interface BookmarkStreamsForMentionsResponse {
  data: BookmarkStreamsForMentionsResponseData;
}

export interface BookmarkStream {
  color: Nullable<string>;
  id: number;
  label: string;
  selected: boolean;
}

export interface AddNewBookmarkStreamPayload {
  groupColor: string;
  groupLabel: string;
  streamLabel: string;
}

export interface MoveToExistingGroupPayload {
  existingGroupId: number;
  streamLabel: string;
}

export default function useBookmarks(props: BookmarksProps) {
  const { mention, product, sourceStream } = toRefs(props);

  const { mediaBookmarkStreams, socialBookmarkStreams } =
    storeToRefs(useStreamsStore());

  const { selectedTeamPreference } = useTeamPicker();

  const existingBookmarkStreams = computed<Stream[]>(() => {
    if (
      (isStream(sourceStream.value) &&
        (sourceStream.value.social_bookmarks_stream ||
          isSocialStream(sourceStream.value))) ||
      product.value === "social"
    ) {
      return socialBookmarkStreams.value;
    }

    return mediaBookmarkStreams.value;
  });

  const bookmarksForMention = ref<Bookmark[]>([]);

  const groupId = ref<Nullable<number>>(null);

  const label = ref<string>("");

  const filterLabel = computed(() =>
    existingBookmarkStreams.value
      .filter((stream) =>
        stream.label.toLowerCase().includes(label.value.toLowerCase())
      )
      .slice()
      .sort((streamA, streamB) => (streamA.label > streamB.label ? 1 : -1))
  );

  function getExistingBookmarkForStream(
    streamId: number
  ): Bookmark | undefined {
    return bookmarksForMention.value.find(
      (bookmarkStream) => bookmarkStream.stream_id === streamId
    );
  }

  function isBookmarkStreamSelected(streamId: number) {
    return Boolean(getExistingBookmarkForStream(streamId));
  }

  async function getBookmarkStreamsForMention() {
    const response = await $streemApiV1.get<Bookmark[]>("bookmarks/search", {
      params: {
        target_id: mention.value.id,
        target_type: targetType(mention.value),
      },
    });

    if (features.has("has_team_workspaces")) {
      bookmarksForMention.value = (response.data || []).filter(
        (bookmark) =>
          bookmark.organisation_team_id === selectedTeamPreference.value ||
          !bookmark.organisation_team_id
      );
    } else {
      bookmarksForMention.value = response.data || [];
    }
  }

  async function updateBookmarkStreamsForMention(bookmarkStream?: Stream) {
    const existingBookmark = bookmarkStream
      ? getExistingBookmarkForStream(bookmarkStream.id)
      : undefined;

    if (existingBookmark && bookmarkStream) {
      await useBookmarkStreamsStore().removeFromBookmarkStream({
        bookmarkStream,
        mention: { ...existingBookmark, bookmark_id: existingBookmark.id },
      });
    } else {
      await useBookmarkStreamsStore().addToBookmarkStream({
        groupId: groupId.value,
        id: bookmarkStream?.id,
        label: label.value,
        mention: mention.value,
        sourceStream: sourceStream.value,
        ...(features.has("has_team_workspaces")
          ? { organisationTeamId: selectedTeamPreference.value }
          : {}),
      });
    }

    label.value = "";

    // Reload the list to display updated folder selection for a mention
    getBookmarkStreamsForMention();
  }

  onMounted(getBookmarkStreamsForMention);

  return {
    bookmarksForMention,
    existingBookmarkStreams,
    filterLabel,
    getBookmarkStreamsForMention,
    getExistingBookmarkForStream,

    groupId,
    isBookmarkStreamSelected,
    label,
    updateBookmarkStreamsForMention,
  };
}
